import { useQuery } from '@tanstack/react-query';
import type { StorageApi } from '@/lib/api';
import { fetchAllStorageFiles } from '../utils';

export interface StorageGroup {
  name: string;
  prefix: string;
  fileCount?: number;
}

/**
 * Fetches top-level groups (folders) and root-level file count for a storage arche.
 * Auto-paginates to capture all folders even when there are >1000 objects.
 * Does NOT fetch files inside each group -- that is done lazily by useStorageFiles
 * when the group is expanded, avoiding N+1 requests on page load.
 */
export function useStorageGroups(api: StorageApi, queryKey: readonly string[]) {
  return useQuery({
    queryKey: [...queryKey],
    queryFn: async () => {
      const root = await fetchAllStorageFiles(api, '', '/');
      const cdnBaseUrl = root.cdnBaseUrl;

      const groups: StorageGroup[] = root.folders.map((folder) => ({
        name: folder.replace(/\/$/, ''),
        prefix: folder,
      }));

      // Include root-level files as a group if any exist
      if (root.files.length > 0) {
        groups.push({
          name: '(root)',
          prefix: '',
          fileCount: root.files.length,
        });
      }

      return { groups, cdnBaseUrl };
    },
  });
}
